import React, { useEffect, useCallback } from "react";
import { MultiSearchForm } from "../../pages/MultiSearch/MultiSearchForm";
import { MultiSearchVariableSelect } from "../../pages/MultiSearch/MultiSearchVariableSelect";
import { FormControl, ControlLabel, Collapse } from "react-bootstrap";
import { ResultsCombinations } from "../../pages/MultiSearch/ResultsCombinations";
import { ResultsCubes } from "../../pages/MultiSearch/ResultsCubes";
import { MULTISEARCH_USAGE } from "src/stores/MultiSearchStore/MultiSearchStore";
import { Observer } from "mobx-react";

import "./SimilarObjects.scss";
import { useState } from "react";

export const SimilarObjects = ({
  entityDetails,
  multiSearchStore,
  entityDetailsParams,
  innerRef,
}) => {
  const [entityType] = useState(entityDetails.object.type);
  const [activeResultsTab, setActiveResultsTab] = useState("cubes");
  const [isVariableListLoading, setVariableListLoading] = useState(true);
  const [isComponentOpen] = useState(true);

  const isCombination = entityType === "Combination";
  const isCube = entityType === "Cube";
  const entityTid = entityDetails.object.tid;
  const setup = useCallback(() => {
    if (
      (entityDetailsParams.relatedType !== "CubeStructure" && entityDetailsParams.relatedType !== "Combination") ||
      !entityDetails ||
      !entityDetails.navigationLookupResult ||
      !entityDetails.navigationLookupResult.navigationLookupRootItem ||
      !entityDetails.navigationLookupResult.navigationLookupRootItem.items ||
      entityDetails.otherInformation.maintenanceAgency?.length < 1
    ) {
      return;
    }

    multiSearchStore.form.resetForm();
    multiSearchStore.form.processAllowedFilters([]);

    setVariableListLoading(true);
    multiSearchStore.setEntityTid(entityTid);

    let items = [];
    if (isCombination) {
      multiSearchStore.setTypesConfiguration("FETCH_COMBINATION", true);
      multiSearchStore.setTypesConfiguration("FETCH_MACRO_CUBE", false);
      multiSearchStore.setTypesConfiguration("FETCH_MICRO_CUBE", false);
      setActiveResultsTab("combinations");

      items =
        entityDetails.navigationLookupResult.navigationLookupRootItem.items.filter(
          (item) => item.object.type === "CombinationItem"
        );
    } else if (isCube) {
      multiSearchStore.setTypesConfiguration("FETCH_COMBINATION", false);
      multiSearchStore.setTypesConfiguration("FETCH_MACRO_CUBE", true);
      multiSearchStore.setTypesConfiguration("FETCH_MICRO_CUBE", true);
      setActiveResultsTab("cubes");

      const cubeStructures =
        entityDetails.navigationLookupResult.navigationLookupRootItem.items.filter(
          (item) => item.object.type === "CubeStructure"
        );
      items = cubeStructures.reduce((acc, cubeStructure) => {
        const csis = cubeStructure.items.filter(
          (item) => item.object.type === "CubeStructureItem"
        );
        return [...acc, ...csis];
      }, []);
    }
    multiSearchStore.setMultisearchUsage(MULTISEARCH_USAGE.NAVIGATOR_COMPONENT);

    const structuredItems = structureObjectAndItems(items);
    const allowedFilters = buildAllowedFilterOptions(structuredItems);
    multiSearchStore.form.processAllowedFilters(allowedFilters);
    multiSearchStore.setSelectedMaintenanceAgency(
      entityDetails.otherInformation.maintenanceAgency[0].tid
    );

    // select all variables by default
    const allowedFilterTIDs = allowedFilters
      .filter((filter) => !!filter.variable.tid)
      .map((filter) => filter.variable.tid); // variable TIDs
    multiSearchStore.form.addFilterItemsBulk(allowedFilterTIDs);

    // select all allowed values of enumerated variables by default
    allowedFilters.forEach((allowedFilter) => {
      if (allowedFilter.options) {
        const variableTid = allowedFilter.variable?.tid;
        if (variableTid) {
          const optionsWithConfig = allowedFilter.options.map((option) => {
            return {
              objectId: option.id,
              isIgnored: false,
              mustMatch: false,
            };
          });
          multiSearchStore.form.updateFilterOptions(
            optionsWithConfig,
            variableTid,
            false
          );
        }
      }
    });

    if (allowedFilters?.length > 0) {
      setVariableListLoading(false);
    }

    multiSearchStore.form.query();

    // on component unmount
    return () => {
      multiSearchStore.form.resetForm();
    };
  }, [
    entityDetails,
    entityDetailsParams,
    multiSearchStore,
    entityTid,
    isCombination,
    isCube,
  ]);

  useEffect(() => {
    setup();
  }, [setup]);

  return (
    <Observer>
      {() => {
        return (
          <div
            ref={innerRef}
            className="related-objects custom-panel similar-objects"
          >
            <div
              className="custom-panel-heading"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex" }}>
                <h4>Similar objects</h4>
              </div>
            </div>

            <Collapse in={isComponentOpen}>
              <div className="custom-panel-body related-types-list">
                <div className="row">
                  <div className="col-xs-4 MultiSearchPage_column">
                    <MultiSearchVariableSelect
                      store={multiSearchStore.form}
                      loading={isVariableListLoading}
                      usage={MULTISEARCH_USAGE.NAVIGATOR_COMPONENT}
                    />
                  </div>
                  <div className="col-xs-8 MultiSearchPage_column">
                    <section>
                      <MultiSearchForm
                        store={multiSearchStore.form}
                        disabled={multiSearchStore.loadingQuery}
                        usage={MULTISEARCH_USAGE.NAVIGATOR_COMPONENT}
                      />
                    </section>
                  </div>
                </div>

                <section>
                  <div className="custom-panel">
                    <div className="custom-panel-heading">
                      <div className="MultiSearchPage__results-title-row">
                        <h4>Search Results</h4>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "10px 10px 0 10px",
                        width: "23%",
                        display: "flex",
                      }}
                    >
                      <ControlLabel style={{ width: "70%", marginTop: "8px" }}>
                        Similarity threshold percentage:
                      </ControlLabel>
                      <FormControl
                        style={{ width: "30%", minWidth: "65px" }}
                        type="number"
                        min={0}
                        max={100}
                        value={
                          multiSearchStore.getSimilarityThresholdPercentage
                        }
                        onChange={(e) =>
                          multiSearchStore.setSimilarityThresholdPercentage(
                            e.target.value
                          )
                        }
                      />
                    </div>
                    <div className="custom-panel-body">
                      {multiSearchStore.loadingQuery && (
                        <div id="ecb_appjs_spinner_element">
                          <div className="loader"></div>
                        </div>
                      )}
                      <div id="multi-search-tabs">
                        <ul className="nav nav-tabs" role="tablist">
                          {isCube && (
                            <li
                              role="presentation"
                              className={
                                activeResultsTab === "cubes" ? "active" : ""
                              }
                            >
                              <a
                                href={() => false}
                                aria-controls="Cubes"
                                role="tab"
                                onClick={() => setActiveResultsTab("cubes")}
                              >
                                Cubes
                                {`(${multiSearchStore.allCubesResults.length})`}
                              </a>
                            </li>
                          )}
                          {isCombination && (
                            <li
                              role="presentation"
                              className={
                                activeResultsTab === "combinations"
                                  ? "active"
                                  : ""
                              }
                            >
                              <a
                                href={() => false}
                                aria-controls="Combinations"
                                role="tab"
                                onClick={() =>
                                  setActiveResultsTab("combinations")
                                }
                              >
                                Combinations [Series keys / Data points]{" "}
                                {`${
                                  multiSearchStore.combinationsResults !== null
                                    ? `(${multiSearchStore.combinationsResults.length})`
                                    : ""
                                }`}
                              </a>
                            </li>
                          )}
                        </ul>
                        <div className="tab-content">
                          {!multiSearchStore.loadingQuery && isCube && (
                            <div
                              role="tabpanel"
                              className={`tab-pane ${
                                activeResultsTab === "cubes" ? "active" : ""
                              }`}
                              id="Cubes"
                            >
                              {multiSearchStore.allCubesResults !== null &&
                                multiSearchStore.dataModelsWithCubes.length !==
                                  0 && (
                                  <div className="MultiSearchPage__results-header-row row">
                                    <div className="col-xs-2">Framework</div>
                                    <div className="col-xs-10">
                                      Data Model with Cubes
                                    </div>
                                  </div>
                                )}
                              {multiSearchStore.allCubesResults !== null && (
                                <ResultsCubes
                                  dataModelsWithCubes={
                                    multiSearchStore.dataModelsWithCubes
                                  }
                                  maintenanceAgency={
                                    entityDetails.otherInformation
                                      .maintenanceAgency[0].maintenanceAgencyId
                                  }
                                />
                              )}
                            </div>
                          )}
                          {!multiSearchStore.loadingQuery && isCombination && (
                            <div
                              role="tabpanel"
                              className={`tab-pane ${
                                activeResultsTab === "combinations"
                                  ? "active"
                                  : ""
                              }`}
                              id="Combinations"
                            >
                              {multiSearchStore.combinationsResults !==
                                null && (
                                <ResultsCombinations
                                  combinationsResults={
                                    multiSearchStore.combinationsResults
                                  }
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </Collapse>
          </div>
        );
      }}
    </Observer>
  );
};

const structureObjectAndItems = (structureItems) => {
  const structuredItems = [];

  structureItems.forEach((structureItem) => {
    const itemAndItems = {
      itemRelatedVariable: undefined, // variable of the related item
      allowedValues: [], // child objects (i.e. members, variables, ...)
    };

    if (structureItem.items && structureItem.items.length) {
      const variable = structureItem.items.find(
        (x) => x.object.type === "Variable"
      );
      const subdomain = structureItem.items.find(
        (x) => x.object.type === "Subdomain"
      );
      const variableSet = structureItem.items.find(
        (x) => x.object.type === "VariableSet"
      );
      const member = structureItem.items.find(
        (x) => x.object.type === "Member"
      );

      itemAndItems.itemRelatedVariable = variable.object;

      if (variableSet) {
        itemAndItems.allowedValues =
          variableSet.items && variableSet.items.length
            ? variableSet.items
                .map((vseItem) => vseItem.items) // nested items
                .reduce((acc, it) => [...acc, ...it]) // flatten list of nested items
                .filter((x) => x.object.type === "Variable") // keep only variables
            : [];
      } else if (member) {
        itemAndItems.allowedValues = [member];
      } else if (subdomain) {
        itemAndItems.allowedValues = subdomain?.items?.length
          ? subdomain.items
              .map((vseItem) => vseItem.items)
              .reduce((acc, it) => [...acc, ...(it || [])], [])
              .filter((x) => x.object?.type === "Member")
          : [];
      }
    }
    structuredItems.push(itemAndItems);
  });

  return structuredItems.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (x) => x.itemRelatedVariable.tid === item.itemRelatedVariable.tid
      )
  );
};

const buildAllowedFilterOptions = (structuredItems) => {
  const filters = [];

  (structuredItems ?? []).forEach((structuredItem) => {
    const { allowedValues, itemRelatedVariable } = structuredItem;
    const filter = {
      options: [],
    };

    const variableFilter = {
      type: itemRelatedVariable.type,
      tid: itemRelatedVariable.tid,
      id: itemRelatedVariable.variableId,
      code: itemRelatedVariable.code,
      name: itemRelatedVariable.name,
      description: itemRelatedVariable.description,
    };
    filter.variable = variableFilter;

    (allowedValues ?? []).forEach((allowedValueObj) => {
      const allowedValue = allowedValueObj.object;
      const type = allowedValue.type;
      let allowedValueId;
      if (type === "Variable") {
        allowedValueId = allowedValue.variableId;
      } else if (type === "Subdomain") {
        allowedValueId = allowedValue.subdomainId;
      } else if (type === "VariableSet") {
        allowedValueId = allowedValue.variableSetId;
      } else if (type === "Member") {
        allowedValueId = allowedValue.memberId;
      }

      const option = {
        type: type,
        tid: allowedValue.tid,
        id: allowedValueId,
        code: allowedValue?.code,
        name: allowedValue?.name,
        description: allowedValue?.description,
      };
      filter.options.push(option);
    });

    filters.push(filter);
  });

  return filters;
};
